// src/admin/pages/carmodel/carModelService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { CarModel, Trim, CarModelFormData } from './carModel';
import { fetchBrandById } from '../brand/brandService';

const CAR_MODELS_COLLECTION = 'carModels';

export interface PaginatedCarModelsResult {
  carModels: CarModel[];
  totalCount: number;
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface CarModelStats {
  total: number;
  active: number;
  inactive: number;
  totalTrims: number;
}

const mapDoc = (docSnap: QueryDocumentSnapshot<DocumentData> | DocumentData, id: string): CarModel => {
  const data = docSnap.data ? docSnap.data() : docSnap;
  return {
    id,
    name: data.name || '',
    brandId: data.brandId || '',
    brandName: data.brandName || '',
    isActive: data.isActive !== undefined ? data.isActive : true,
    trims: data.trims || [],
    createdAt: data.createdAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
    createdBy: data.createdBy,
  };
};

export const fetchCarModelsPaginated = async (
  pageSize: number = 10,
  page: number = 1,
  searchQuery?: string
): Promise<PaginatedCarModelsResult> => {
  try {
    const carModels: CarModel[] = [];
    const collectionRef = collection(db, CAR_MODELS_COLLECTION);
    const orderConstraint = orderBy('createdAt', 'desc');

    const countSnapshot = await getCountFromServer(query(collectionRef));
    const totalCount = countSnapshot.data().count;

    let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
    const skipCount = (page - 1) * pageSize;

    if (skipCount > 0) {
      const seekSnapshot = await getDocs(query(collectionRef, orderConstraint, limit(skipCount)));
      if (!seekSnapshot.empty) {
        cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
      }
    }

    const pageQuery: Query<DocumentData> = cursorDoc
      ? query(collectionRef, orderConstraint, startAfter(cursorDoc), limit(pageSize))
      : query(collectionRef, orderConstraint, limit(pageSize));

    const snapshot = await getDocs(pageQuery);

    snapshot.docs.forEach(docSnap => {
      const carModel = mapDoc(docSnap, docSnap.id);

      if (searchQuery && searchQuery.trim() !== '') {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch =
          carModel.name.toLowerCase().includes(searchLower) ||
          (carModel.brandName || '').toLowerCase().includes(searchLower) ||
          carModel.trims.some(t => t.name.toLowerCase().includes(searchLower));
        if (matchesSearch) carModels.push(carModel);
      } else {
        carModels.push(carModel);
      }
    });

    return { carModels, totalCount, lastVisible: null, firstVisible: null };
  } catch (error) {
    console.error('Error fetching paginated car models:', error);
    throw error;
  }
};

export const fetchCarModelStats = async (): Promise<CarModelStats> => {
  try {
    const col = collection(db, CAR_MODELS_COLLECTION);

    const [totalSnap, activeSnap, inactiveSnap, allDocsSnap] = await Promise.all([
      getCountFromServer(query(col)),
      getCountFromServer(query(col, where('isActive', '==', true))),
      getCountFromServer(query(col, where('isActive', '==', false))),
      getDocs(query(col)),
    ]);

    const totalTrims = allDocsSnap.docs.reduce(
      (sum, d) => sum + ((d.data().trims as any[])?.length || 0),
      0
    );

    return {
      total: totalSnap.data().count,
      active: activeSnap.data().count,
      inactive: inactiveSnap.data().count,
      totalTrims,
    };
  } catch (error) {
    console.error('Error fetching car model stats:', error);
    throw error;
  }
};

export const createCarModel = async (
  formData: CarModelFormData,
  userId: string
): Promise<{ success: boolean; message: string; carModelId?: string }> => {
  try {
    if (!formData.name.trim()) throw new Error('Model name is required');
    if (!formData.brandId) throw new Error('A brand must be selected');

    const dupSnap = await getDocs(
      query(
        collection(db, CAR_MODELS_COLLECTION),
        where('name', '==', formData.name.trim()),
        where('brandId', '==', formData.brandId)
      )
    );
    if (!dupSnap.empty) {
      return { success: false, message: 'A model with this name already exists for this brand' };
    }

    const brand = await fetchBrandById(formData.brandId);

    const docRef = await addDoc(collection(db, CAR_MODELS_COLLECTION), {
      name: formData.name.trim(),
      brandId: formData.brandId,
      brandName: brand?.name || '',
      isActive: formData.isActive !== undefined ? formData.isActive : true,
      trims: formData.trims.map(trim => ({
        id: trim.id || generateId(),
        name: trim.name.trim(),
        description: trim.description?.trim() || null,
        isActive: trim.isActive !== undefined ? trim.isActive : true,
      })),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId,
    });

    return { success: true, message: 'Model created successfully!', carModelId: docRef.id };
  } catch (error: any) {
    console.error('Error creating car model:', error);
    return { success: false, message: error.message || 'Failed to create model.' };
  }
};

export const updateCarModel = async (
  carModelId: string,
  formData: CarModelFormData
): Promise<{ success: boolean; message: string }> => {
  try {
    if (!formData.name.trim()) throw new Error('Model name is required');
    if (!formData.brandId) throw new Error('A brand must be selected');

    const dupSnap = await getDocs(
      query(
        collection(db, CAR_MODELS_COLLECTION),
        where('name', '==', formData.name.trim()),
        where('brandId', '==', formData.brandId)
      )
    );
    const duplicate = dupSnap.docs.find(d => d.id !== carModelId);
    if (duplicate) {
      return { success: false, message: 'Another model with this name already exists for this brand' };
    }

    const brand = await fetchBrandById(formData.brandId);
    const carModelRef = doc(db, CAR_MODELS_COLLECTION, carModelId);
    await updateDoc(carModelRef, {
      name: formData.name.trim(),
      brandId: formData.brandId,
      brandName: brand?.name || '',
      isActive: formData.isActive !== undefined ? formData.isActive : true,
      trims: formData.trims.map(trim => ({
        id: trim.id || generateId(),
        name: trim.name.trim(),
        description: trim.description?.trim() || null,
        isActive: trim.isActive !== undefined ? trim.isActive : true,
      })),
      updatedAt: serverTimestamp(),
    });

    return { success: true, message: 'Model updated successfully!' };
  } catch (error: any) {
    console.error('Error updating car model:', error);
    return { success: false, message: error.message || 'Failed to update model.' };
  }
};

export const deleteCarModel = async (
  carModelId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const carModelRef = doc(db, CAR_MODELS_COLLECTION, carModelId);
    const carModelSnap = await getDoc(carModelRef);

    if (!carModelSnap.exists()) {
      return { success: false, message: 'Model not found.' };
    }

    await deleteDoc(carModelRef);
    return { success: true, message: 'Model deleted successfully!' };
  } catch (error: any) {
    console.error('Error deleting car model:', error);
    return { success: false, message: error.message || 'Failed to delete model.' };
  }
};

export const toggleCarModelStatus = async (
  carModelId: string,
  isActive: boolean
): Promise<{ success: boolean; message: string }> => {
  try {
    const carModelRef = doc(db, CAR_MODELS_COLLECTION, carModelId);
    const snap = await getDoc(carModelRef);
    if (!snap.exists()) return { success: false, message: 'Model not found.' };

    await updateDoc(carModelRef, { isActive, updatedAt: serverTimestamp() });
    return { success: true, message: `Model ${isActive ? 'activated' : 'deactivated'} successfully!` };
  } catch (error: any) {
    console.error('Error toggling model status:', error);
    return { success: false, message: error.message || 'Failed to update status.' };
  }
};

const generateId = (): string =>
  `trim_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const fetchActiveCarModels = async (brandId?: string): Promise<CarModel[]> => {
  try {
    const constraints = [where('isActive', '==', true)];
    if (brandId) constraints.push(where('brandId', '==', brandId));
    const q = query(collection(db, CAR_MODELS_COLLECTION), ...constraints, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => mapDoc(d, d.id));
  } catch (error) {
    console.error('Error fetching active car models:', error);
    throw error;
  }
};

export const fetchCarModelById = async (carModelId: string): Promise<CarModel | null> => {
  try {
    const snap = await getDoc(doc(db, CAR_MODELS_COLLECTION, carModelId));
    if (!snap.exists()) return null;
    return mapDoc(snap, snap.id);
  } catch (error) {
    console.error('Error fetching car model:', error);
    throw error;
  }
};

/** Backwards-compatible class wrapper. */
export class CarModelService {
  static getCarModelsPaginated = fetchCarModelsPaginated;
  static getCarModelStats      = fetchCarModelStats;
  static createCarModel        = createCarModel;
  static updateCarModel        = updateCarModel;
  static deleteCarModel        = deleteCarModel;
  static toggleCarModelStatus  = toggleCarModelStatus;
  static getActiveCarModels    = fetchActiveCarModels;
  static getCarModel           = fetchCarModelById;
}
